import { useEffect, useState, useMemo } from 'react';
import { ppeIssuesAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { formatDate, cn } from '../../lib/utils';
import { HardHat, Eye, FileText, AlertTriangle, Calendar as CalendarIcon, History } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';

export function PPEHistory({ employeeId }) {
  const { user } = useAuth();
  const canEdit = ['admin', 'hr_manager'].includes(user?.role);

  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [editingIssue, setEditingIssue] = useState(null);
  const [editDate, setEditDate] = useState('');
  const [editReplacementDate, setEditReplacementDate] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  // Memoised audit trail entries that contain a date diff. Recomputed only
  // when the selected issue (or its audit trail) changes.
  const dateChangeAuditEntries = useMemo(() => {
    const trail = Array.isArray(selectedIssue?.audit_trail) ? selectedIssue.audit_trail : [];
    return trail.filter(a => a.field_changes && Object.keys(a.field_changes).length > 0);
  }, [selectedIssue]);
  const hasDateChangeAudit = dateChangeAuditEntries.length > 0;

  useEffect(() => {
    if (employeeId) {
      fetchPPEHistory();
    }
  }, [employeeId]);

  const fetchPPEHistory = async () => {
    try {
      const res = await ppeIssuesAPI.getByEmployee(employeeId);
      setIssues(res.data);
    } catch (error) {
      toast.error('Failed to load PPE history');
    } finally {
      setLoading(false);
    }
  };

  const openDetail = (issueRow) => {
    setSelectedIssue(issueRow);
    setShowDetailModal(true);
  };

  const openEditDate = (issue) => {
    setEditingIssue(issue);
    setEditDate((issue.issue_date || '').slice(0, 10));
    setEditReplacementDate((issue.expected_replacement_date || '').slice(0, 10));
  };

  const closeEditDate = () => {
    setEditingIssue(null);
    setEditDate('');
    setEditReplacementDate('');
  };

  const saveEditDate = async () => {
    if (!editingIssue) return;
    if (!editDate) {
      toast.error('Issue Date is required');
      return;
    }
    const currentIssue = (editingIssue.issue_date || '').slice(0, 10);
    const currentReplacement = (editingIssue.expected_replacement_date || '').slice(0, 10);
    const payload = {};
    if (editDate !== currentIssue) payload.issue_date = editDate;
    if (editReplacementDate !== currentReplacement) {
      payload.expected_replacement_date = editReplacementDate || null;
    }
    if (Object.keys(payload).length === 0) {
      toast.info('No changes to save');
      closeEditDate();
      return;
    }
    setEditSaving(true);
    try {
      await ppeIssuesAPI.update(editingIssue.id, payload);
      toast.success('Dates updated');
      closeEditDate();
      fetchPPEHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update dates');
    } finally {
      setEditSaving(false);
    }
  };

  const isOverdue = (date) => {
    if (!date) return false;
    return new Date(date) < new Date();
  };

  const isDueSoon = (date) => {
    if (!date) return false;
    const dueDate = new Date(date);
    const today = new Date();
    const thirtyDaysFromNow = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
    return dueDate >= today && dueDate <= thirtyDaysFromNow;
  };

  if (loading) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        Loading PPE history...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {issues.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <HardHat className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No PPE issued to this employee</p>
        </div>
      ) : (
        <div className="table-scroll">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Date Issued</TableHead>
              <TableHead>Item</TableHead>
              <TableHead>Qty</TableHead>
              <TableHead>Size</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead>Replacement Due</TableHead>
              <TableHead>Issued By</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {issues.map((issue) => (
              <TableRow 
                key={issue.id} 
                className={cn(
                  "table-row-hover",
                  issue.voided && "opacity-50"
                )}
                data-testid={`ppe-history-row-${issue.id}`}
              >
                <TableCell className="font-medium">
                  {formatDate(issue.issue_date)}
                </TableCell>
                <TableCell>
                  <div>
                    <p className="font-medium">{issue.ppe_item_name}</p>
                    <p className="text-xs text-muted-foreground">{issue.ppe_item_category}</p>
                  </div>
                </TableCell>
                <TableCell>{issue.quantity} {issue.unit_of_issue}</TableCell>
                <TableCell>{issue.size || '-'}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={cn(
                    "border text-xs",
                    issue.reason_for_issue === 'Lost' && "bg-red-500/20 text-red-400 border-red-500/30",
                    issue.reason_for_issue === 'Damaged' && "bg-amber-500/20 text-amber-400 border-amber-500/30",
                    issue.reason_for_issue === 'New issue' && "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
                  )}>
                    {issue.reason_for_issue}
                  </Badge>
                </TableCell>
                <TableCell>
                  {issue.expected_replacement_date ? (
                    <div className="flex items-center gap-2">
                      <span className={cn(
                        isOverdue(issue.expected_replacement_date) && "text-red-400",
                        isDueSoon(issue.expected_replacement_date) && "text-amber-400"
                      )}>
                        {formatDate(issue.expected_replacement_date)}
                      </span>
                      {isOverdue(issue.expected_replacement_date) && (
                        <AlertTriangle className="w-4 h-4 text-red-400" />
                      )}
                    </div>
                  ) : '-'}
                </TableCell>
                <TableCell className="text-muted-foreground text-sm">
                  {issue.issued_by_name}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    {canEdit && !issue.voided && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEditDate(issue)}
                        title="Edit Issue Date"
                        data-testid={`edit-issue-date-btn-${issue.id}`}
                      >
                        <CalendarIcon className="w-4 h-4" />
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" onClick={() => openDetail(issue)}>
                      <Eye className="w-4 h-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </div>
      )}

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              PPE Issue Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedIssue && (
            <div className="space-y-4">
              {selectedIssue.voided && (
                <div className="p-3 rounded-lg bg-red-500/20 border border-red-500/30">
                  <p className="text-red-400 font-medium">This issue has been voided</p>
                  <p className="text-sm text-red-400/80">{selectedIssue.void_reason}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Issue Date</p>
                  <p className="font-medium">{formatDate(selectedIssue.issue_date)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">PPE Item</p>
                  <p className="font-medium">{selectedIssue.ppe_item_name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Category</p>
                  <p className="font-medium">{selectedIssue.ppe_item_category}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Quantity</p>
                  <p className="font-medium">{selectedIssue.quantity} {selectedIssue.unit_of_issue}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Size</p>
                  <p className="font-medium">{selectedIssue.size || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Condition</p>
                  <p className="font-medium">{selectedIssue.condition_when_issued}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Reason</p>
                  <Badge variant="outline">{selectedIssue.reason_for_issue}</Badge>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Replacement Due</p>
                  <p className={cn(
                    "font-medium",
                    isOverdue(selectedIssue.expected_replacement_date) && "text-red-400"
                  )}>
                    {selectedIssue.expected_replacement_date ? formatDate(selectedIssue.expected_replacement_date) : 'Not set'}
                  </p>
                </div>
                {selectedIssue.serial_batch_number && (
                  <div>
                    <p className="text-sm text-muted-foreground">Serial/Batch #</p>
                    <p className="font-medium">{selectedIssue.serial_batch_number}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-muted-foreground">Issued By</p>
                  <p className="font-medium">{selectedIssue.issued_by_name}</p>
                </div>
              </div>

              {selectedIssue.notes && (
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Notes</p>
                  <p className="text-foreground bg-secondary/30 p-3 rounded-lg">{selectedIssue.notes}</p>
                </div>
              )}

              <div className="border-t border-border pt-4">
                <p className="text-sm text-muted-foreground mb-2">Acknowledgement</p>
                <div className="bg-secondary/30 p-3 rounded-lg">
                  <p className="text-sm">
                    <span className="font-medium">Type:</span> {selectedIssue.acknowledgement_type === 'signature' ? 'Digital Signature' : selectedIssue.acknowledgement_type === 'typed' ? 'Typed Acknowledgement' : 'Issuer Confirmation'}
                  </p>
                  {selectedIssue.acknowledgement_type === 'signature' && selectedIssue.acknowledgement_data && (
                    <img 
                      src={selectedIssue.acknowledgement_data} 
                      alt="Employee Signature" 
                      className="mt-2 border border-border rounded bg-white max-h-24"
                    />
                  )}
                  {selectedIssue.acknowledgement_type === 'typed' && (
                    <p className="mt-1 italic">&quot;{selectedIssue.acknowledgement_data}&quot;</p>
                  )}
                </div>
              </div>

              <div className="text-xs text-muted-foreground">
                <p>Created: {formatDate(selectedIssue.created_at)}</p>
                {selectedIssue.updated_at !== selectedIssue.created_at && (
                  <p>Last updated: {formatDate(selectedIssue.updated_at)}</p>
                )}
              </div>

              {/* Audit trail for date corrections (admins only) */}
              {canEdit && hasDateChangeAudit && (
                <div className="border-t border-border pt-4">
                  <p className="text-sm text-muted-foreground mb-2 flex items-center gap-1">
                    <History className="w-3.5 h-3.5" /> Date change history
                  </p>
                  <div className="bg-secondary/30 p-3 rounded-lg space-y-2 text-xs">
                    {dateChangeAuditEntries.map((a) => (
                      <div key={a.timestamp} className="border-b border-border/40 last:border-b-0 pb-2 last:pb-0">
                        <p className="text-muted-foreground">
                          {formatDate(a.timestamp)} · {a.user_name}
                        </p>
                        {Object.entries(a.field_changes).map(([f, change]) => (
                          <p key={f}>
                            <span className="font-medium capitalize">{f.replace(/_/g, ' ')}:</span>
                            {' '}
                            <span className="line-through opacity-60">{change.old || '—'}</span>
                            {' → '}
                            <span className="text-emerald-400">{change.new || '—'}</span>
                          </p>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Issue Date Modal */}
      <Dialog open={!!editingIssue} onOpenChange={(v) => !v && closeEditDate()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 text-primary" />
              Edit PPE Dates
            </DialogTitle>
          </DialogHeader>
          {editingIssue && (
            <div className="space-y-4">
              <div className="bg-secondary/30 p-3 rounded-lg text-sm">
                <p className="font-medium">{editingIssue.ppe_item_name}</p>
                <p className="text-muted-foreground">
                  {editingIssue.employee_name} · {editingIssue.quantity} {editingIssue.unit_of_issue}
                </p>
              </div>
              <div>
                <Label htmlFor="edit-issue-date">Issue Date</Label>
                <Input
                  id="edit-issue-date"
                  type="date"
                  value={editDate}
                  onChange={(e) => setEditDate(e.target.value)}
                  max={new Date().toISOString().slice(0, 10)}
                  data-testid="edit-issue-date-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Current: {formatDate(editingIssue.issue_date)}
                </p>
              </div>
              <div>
                <Label htmlFor="edit-replacement-date">Replacement Date</Label>
                <Input
                  id="edit-replacement-date"
                  type="date"
                  value={editReplacementDate}
                  onChange={(e) => setEditReplacementDate(e.target.value)}
                  data-testid="edit-replacement-date-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Current: {editingIssue.expected_replacement_date ? formatDate(editingIssue.expected_replacement_date) : 'Not set'}
                </p>
              </div>
              <p className="text-xs text-muted-foreground">
                Each change is recorded against your user account and will appear in the issue&apos;s audit trail (Date change history).
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closeEditDate} disabled={editSaving}>
              Cancel
            </Button>
            <Button
              onClick={saveEditDate}
              disabled={editSaving}
              data-testid="save-issue-date-btn"
            >
              {editSaving ? 'Saving…' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
